import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import PasswordStrengthMeter from '../components/PasswordStrengthMeter';
import Seo from '../components/Seo';
import Footer from '../components/Footer';
import { getStoredReferral, clearStoredReferral } from '../utils/referralStorage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Register = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [signupsOpen, setSignupsOpen] = useState(null);  // null = still checking
  // Predictions disclaimer — must be ticked before either button fires.
  // Persisted in localStorage so a Google-OAuth round-trip survives and
  // AuthCallback can forward it to /api/auth/session for new users.
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  // Pulled once on mount from localStorage (set by App's ?ref= capture).
  // If present, we surface a small "you were referred" banner and forward
  // the code to the backend on submit / Google round-trip.
  const [referralCode] = useState(() => getStoredReferral());
  const { register } = useAuth();
  const navigate = useNavigate();

  // Hard gate: if public signups aren't open yet, bounce back to the
  // coming-soon landing so visitors can join the waitlist instead.
  useEffect(() => {
    axios.get(`${API}/feature-flags`)
      .then((r) => {
        const open = !!r.data?.public_signups_enabled;
        setSignupsOpen(open);
        if (!open) navigate('/', { replace: true });
      })
      .catch(() => {
        setSignupsOpen(false);
        navigate('/', { replace: true });
      });
  }, [navigate]);

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!disclaimerAccepted) {
      toast.error('Please confirm you understand how Forge Ledger predictions work.');
      return;
    }
    setLoading(true);

    try {
      await register(email, password, name, true, referralCode);
      clearStoredReferral();
      toast.success('Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      const detail = error.response?.data?.detail;
      let errorMsg = 'Registration failed. Please try again.';
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail)) {
        errorMsg = detail.map((e) => e?.msg || JSON.stringify(e)).join(' ');
      }
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = () => {
    if (!disclaimerAccepted) {
      toast.error('Please confirm you understand how Forge Ledger predictions work.');
      return;
    }
    // Stash the acknowledgement so AuthCallback can forward it to the
    // /session endpoint after Google bounces us back. Cleared on the
    // other side.
    try {
      localStorage.setItem('forge:disclaimer_accepted', '1');
      if (referralCode) {
        // AuthCallback also forwards this to /session for new Google
        // accounts. (referralStorage already has it; this is a belt &
        // braces handoff in case the user wipes one but not the other.)
        localStorage.setItem('forge:ref_code_pending', referralCode);
      }
    } catch (_e) { /* private mode — non-fatal */ }
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH.
    // `auth.emergentagent.com` IS the documented Emergent-managed Google OAuth proxy — intentional
    // and correct. The redirect URL must use window.location.origin (no fallbacks).
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 flex">
        <Seo
          title="Create your free Forge Ledger account"
          description="Open a free Forge Ledger account to budget, reconcile and forecast your money — GBP-first, DD/MM/YYYY, no card required."
          path="/register"
          noindex={signupsOpen === false}
        />
      {signupsOpen !== true ? (
        <div className="flex-1 flex items-center justify-center bg-[#F8FAFC]" data-testid="register-gate-spinner">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#0052FF]" />
        </div>
      ) : (
        <>
        <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <h1 className="text-4xl tracking-tighter font-extrabold text-[#020617] mb-2" data-testid="register-title">
              Create Account
            </h1>
            <p className="text-base text-[#64748B]">Start managing your finances today</p>
          </div>

          {referralCode && (
            <div
              className="mb-6 rounded-lg border border-[#BFDBFE] bg-[#EFF6FF] px-4 py-3 text-sm text-[#1E3A8A] flex items-start gap-3"
              data-testid="referral-banner"
            >
              <span className="text-lg leading-none" aria-hidden="true">🎁</span>
              <div>
                <div className="font-semibold">You were referred to Forge Ledger.</div>
                <div className="text-[#1E40AF] text-xs mt-0.5">
                  Subscribe to any paid Forge Ledger tier and we&apos;ll add £1 of credit
                  to your Forge Ledger billing (applied to your next invoice — not to your
                  bank account) after your first payment, and we&apos;ll thank your friend
                  with £1 too.
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleRegister} className="space-y-6" data-testid="register-form">
            <div>
              <Label htmlFor="name" className="text-sm font-medium text-[#020617]">
                Full Name
              </Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="mt-1.5 bg-white border border-[#E2E8F0] rounded-lg px-4 py-2 text-[#020617] focus:border-[#0052FF] focus:ring-1 focus:ring-[#0052FF]"
                placeholder="John Doe"
                data-testid="register-name-input"
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-sm font-medium text-[#020617]">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1.5 bg-white border border-[#E2E8F0] rounded-lg px-4 py-2 text-[#020617] focus:border-[#0052FF] focus:ring-1 focus:ring-[#0052FF]"
                placeholder="you@example.com"
                data-testid="register-email-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-sm font-medium text-[#020617]">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={10}
                maxLength={128}
                className="mt-1.5 bg-white border border-[#E2E8F0] rounded-lg px-4 py-2 text-[#020617] focus:border-[#0052FF] focus:ring-1 focus:ring-[#0052FF]"
                placeholder="At least 10 characters"
                data-testid="register-password-input"
              />
              <PasswordStrengthMeter password={password} minLength={10} />
            </div>

            {/* Predictions-accuracy disclaimer — UK FCA-friendly paper
                trail. Required before either auth path can fire. */}
            <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="disclaimer-acknowledge">
              <input
                type="checkbox"
                checked={disclaimerAccepted}
                onChange={(e) => setDisclaimerAccepted(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-[#CBD5E1] text-[#0052FF] focus:ring-[#0052FF]"
                data-testid="disclaimer-checkbox"
              />
              <span className="text-sm text-[#475569] leading-relaxed">
                I understand Forge Ledger&apos;s forecasts are simple mathematical
                projections built from the data <strong>I</strong> enter — recurring
                transactions, balances and one-offs. They&apos;re only as accurate as
                what I provide, and aren&apos;t financial advice.
              </span>
            </label>

            <Button
              type="submit"
              disabled={loading || !disclaimerAccepted}
              className="w-full bg-[#0052FF] text-white rounded-lg px-6 py-2.5 font-medium hover:bg-[#003ECC] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="register-submit-button"
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </Button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-[#E2E8F0]"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-[#F8FAFC] text-[#64748B]">Or continue with</span>
              </div>
            </div>

            <Button
              type="button"
              onClick={handleGoogleSignup}
              disabled={!disclaimerAccepted}
              className="w-full mt-4 bg-white border border-[#E2E8F0] text-[#020617] rounded-lg px-6 py-2.5 font-medium hover:bg-[#F8FAFC] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="google-signup-button"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Sign up with Google
            </Button>
          </div>

          <p className="mt-6 text-center text-sm text-[#64748B]">
            Already have an account?{' '}
            <Link to="/login" className="text-[#0052FF] font-medium hover:text-[#003ECC]">
              Sign in
            </Link>
          </p>
          <p className="mt-3 text-center text-xs text-[#94A3B8]">
            <Link to="/security" className="hover:text-[#0052FF]" data-testid="register-security-link">
              How we keep your data safe →
            </Link>
          </p>
        </div>
      </div>

      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-[#0052FF] to-[#003ECC] items-center justify-center p-12">
        <div className="max-w-md text-white">
          <h2 className="text-3xl font-bold mb-4">Smart Financial Management</h2>
          <p className="text-lg opacity-90">
            Get insights into your spending patterns, set up recurring transactions, and receive shortfall warnings.
          </p>
        </div>
      </div>
        </>
      )}
      </div>
      <Footer variant="full" />
    </div>
  );
};

export default Register;
